import { baseApi } from "./baseApi";
import { retryTransientOnce, retryTransientOnly } from "./retryTransientOnly";
import type { TextingNumberStatus } from "../../../shared/employee/sms";
import type {
  EmpAttachment,
  EmpConversation,
  EmpMessage,
  EmpMessageReport,
} from "../../../shared/employee/messages";
import type { Role } from "../../../shared/roles";
import type {
  EmpDueItem,
  EmpSurveyHistoryItem,
  EmpSurvey,
  EmpSurveyAnswer,
  EmpSurveyDetail,
  EmpSurveyInput,
  EmpSurveyQuestionInput,
  EmpSurveyResults,
  EmpShareSurveyResultsInput,
  EmpSurveyToAnswer,
} from "../../../shared/employee/surveys";
import type {
  EmpAudience,
  EmpErrorCode,
  EmpMe,
  EmpMyProfile,
  EmpMyScopeEntry,
  EmpScopeEntry,
} from "../../../shared/employee/access";
import type {
  EmpLocationsOverview,
  EmpMemberPage,
  EmpMemberStatus,
  EmpMemberSummary,
  EmpScopeEntryView,
} from "../../../shared/employee/admin";
import type { EmpPersonContact } from "../../../shared/employee/contacts";
import type { EmpRegulationList } from "../../../shared/employee/regulations";
import type {
  EmpContactField,
  EmpDirectoryVisibility,
  EmpPortalRef,
  EmpSettings,
} from "../../../shared/employee/settings";
import type {
  EmpDirectoryFilters,
  EmpDirectoryPage,
  EmpMyScopePeople,
  EmpPersonProfile,
  EmpOrgPeople,
  EmpOrgPerson,
  EmpOrgView,
} from "../../../shared/employee/people";
import type {
  EmpNotificationPage,
  EmpNotificationPrefs,
} from "../../../shared/employee/notifications";
import type { EmpSearchResults } from "../../../shared/employee/search";
import type {
  EmpExternalApp,
  EmpExternalAppInput,
  EmpMyApps,
  EmpMyAppsAdmin,
} from "../../../shared/employee/apps";
import type { EmpOverview } from "../../../shared/employee/overview";
import type { EmpMilestone, EmpMyBirthday } from "../../../shared/employee/milestones";
import type {
  EmpBadge,
  EmpBadgeAdmin,
  EmpBadgeInput,
  EmpEcardDesign,
  EmpEcardDesignAdmin,
  EmpEcardDesignInput,
} from "../../../shared/employee/recognition";
import type { EmpEcardPage, EmpNewEcard } from "../../../shared/employee/ecards";
import type {
  EmpCodeLoadInput,
  EmpCodeLoadResult,
  EmpGiftCodePage,
  EmpGiftCodeFilter,
  EmpGiftSettings,
  EmpBudgets,
  EmpManagerAllowance,
  EmpAwardableType,
  EmpAwardApproval,
  EmpMyReward,
  EmpFulfilmentItem,
  EmpAwardAdminPage,
  EmpAwardTotals,
  EmpAwardStatusFilter,
  EmpGiftAuditPage,
  EmpGiftOverview,
  EmpFulfilmentStep,
  EmpRevealedCode,
  EmpAwardGivenPage,
  EmpAwardInput,
  EmpAwardResult,
  EmpMyAllowance,
  EmpRewardTypeAdmin,
  EmpRewardTypeInput,
} from "../../../shared/employee/giftcards";
import type {
  EmpBadgeCount,
  EmpRecentBadges,
  EmpRecognitionPage,
} from "../../../shared/employee/recognitionReceived";
import type { EmpLeaderboard, EmpLeaderboardPeriod } from "../../../shared/employee/leaderboard";
import type {
  ChatUploadLimits,
  MessagingSectionKey,
  MessagingSectionState,
} from "../../../shared/employee/messagingSettings";
import type {
  EmpAckTrackerDetail,
  EmpAckTrackerRow,
  EmpPastReview,
  EmpReaderCategory,
  EmpResource,
  EmpResourceAdmin,
  EmpResourceCategory,
  EmpResourceCategoryInput,
  EmpResourceInput,
  EmpResourceView,
} from "../../../shared/employee/resources";
import type {
  EmpBanner,
  EmpBannerInput,
  EmpBannerLinkKind,
  EmpBannerLinkOption,
  EmpManagedBanner,
} from "../../../shared/employee/banners";
import type {
  EmpComment,
  EmpCommentPage,
  EmpFeedPage,
  EmpFeedPost,
  EmpLikeState,
  EmpLikersPage,
  EmpPollResultsMode,
  EmpPostType,
} from "../../../shared/employee/feed";
import type { EmpVideoPlayback } from "../../../shared/employee/media";
import type {
  EmpModerationAction,
  EmpReportQueuePage,
  EmpReportReason,
  EmpReportTarget,
} from "../../../shared/employee/moderation";
import type {
  EmpContentStatus,
  EmpManagedPostChange,
  EmpManagedPostPage,
  EmpManagedPostType,
} from "../../../shared/employee/content";

/**
 * skyEmployee API. Every response is `{ data }`; errors are `{ error, code }`.
 * The server enforces access; what the client reads from getEmployeeMe is for
 * display only (which menu items and buttons to show).
 */

export interface EmployeeDirectoryParams {
  q?: string;
  locationId?: string;
  departmentId?: string;
  page?: number;
  pageSize?: number;
  /** Only the people at the locations and teams the caller manages. */
  inMyScope?: boolean;
}

export interface EmployeeSettingsPatch {
  directoryVisibility?: EmpDirectoryVisibility;
  publishedContactFields?: EmpContactField[];
  employeePortalId?: string | null;
  myApps?: { hidden: string[]; order: string[] };
  orgName?: string | null;
}

export interface EmployeeMemberListParams {
  q?: string;
  /** Access Management role (e.g. skypoint_admin). */
  platformRole?: Role;
  status?: EmpMemberStatus;
  locationId?: string;
  page?: number;
  pageSize?: number;
}

/** The composer's post (sent as JSON in the "post" field, with photos). */
export interface EmployeeNewPost {
  type: "post" | "shout_out" | "announcement" | "spotlight";
  title?: string | null;
  body: string;
  mentionPartyIds?: string[];
  recipientPartyIds?: string[];
  featuredPartyId?: string | null;
  audience?: EmpAudience;
  pinnedUntil?: string | null;
  commentsOff?: boolean;
  publishAt?: string | null;
  endsAt?: string | null;
  /** Posts: a video uploaded beforehand (see videoUpload.ts). */
  videoId?: string | null;
  /** Posts and announcements: files uploaded beforehand, in order (see videoUpload.ts). */
  fileIds?: string[];
}

export interface EmployeeNewPoll {
  question: string;
  options: string[];
  body?: string | null;
  anonymous?: boolean;
  resultsMode?: EmpPollResultsMode;
  closesAt: string;
  audience?: EmpAudience;
  commentsOff?: boolean;
}

export interface EmployeeLocationRecord {
  id: string;
  name: string;
  source: "custom" | "entra";
  status: "active" | "archived";
}

const unwrap = <T>(res: { data: T }): T => res.data;

/**
 * Any gift card change (an award, an approval, a step, codes, types, budgets)
 * can move what several gift card screens show (Overview, To hand over,
 * Give, Approvals), so it refreshes them all. Revealing a code isn't one.
 */
const GIFTCARDS_CHANGED = ["EmployeeGiftcards" as const];

/**
 * The feed's first pages: the employee view's (LIST) and the admin view's
 * (MANAGE). A new post belongs in both; a change made from a post in the admin
 * feed patches that feed in place and refreshes only the employee view's.
 */
const EMPLOYEE_FEED = { type: "EmployeeFeed" as const, id: "LIST" };
const ADMIN_FEED = { type: "EmployeeFeed" as const, id: "MANAGE" };
const BOTH_FEEDS = [EMPLOYEE_FEED, ADMIN_FEED];
/** Featured videos (Home's rail and Manage → Content). */
const FEATURED_VIDEOS = { type: "EmployeeFeed" as const, id: "FEATURED" };

/**
 * Recognition people have received (the leaderboard, badge counts, awards,
 * my recognition): a new, deleted or moderated shout-out changes them.
 */
const RECEIVED = { type: "EmployeeRecognition" as const, id: "RECEIVED" };

/**
 * One page of the feed. `view: "manage"` is the admin view's (also the posts
 * the caller moderates, hidden ones included), optionally for one location.
 */
export interface EmployeeFeedArgs {
  type?: EmpPostType;
  /** The Videos filter: only posts with a video. */
  media?: "video";
  before?: string;
  view?: "manage";
  locationId?: string;
  /** Only shout-outs naming this person. */
  recipientPartyId?: string;
}

/**
 * Change one post where it's cached (the feed's first pages and the post
 * itself), so coming back to a page within the cache window never shows it
 * as it was. Patched rather than refetched, so an open feed doesn't jump.
 * `next` null takes it out.
 */
function patchCachedPost(
  dispatch: (action: ReturnType<typeof employeeApi.util.updateQueryData>) => unknown,
  getState: () => unknown,
  postId: string,
  /** `args` is the feed page's (absent for the post's own page). */
  next: (post: EmpFeedPost, args?: EmployeeFeedArgs) => EmpFeedPost | null,
): void {
  const state = getState() as Parameters<typeof employeeApi.util.selectCachedArgsForQuery>[0];
  // Every cached page, older ones ("Show more") included.
  for (const args of employeeApi.util.selectCachedArgsForQuery(state, "getEmployeeFeed")) {
    dispatch(
      employeeApi.util.updateQueryData("getEmployeeFeed", args as EmployeeFeedArgs, (page) => {
        const i = page.items.findIndex((p) => p.id === postId);
        if (i < 0) return;
        const changed = next(page.items[i], args as EmployeeFeedArgs);
        if (changed) page.items[i] = changed;
        else page.items.splice(i, 1);
      }),
    );
  }
  dispatch(
    employeeApi.util.updateQueryData("getEmployeePost", postId, (post) => {
      const changed = next(post);
      if (changed) Object.assign(post, changed);
    }),
  );
}

/** The awards filters (#1369 AC7). One type, so the list and the totals agree. */
export interface EmpAwardsAdminArgs {
  status?: EmpAwardStatusFilter;
  quarter?: string;
  locationId?: string;
  departmentId?: string;
  awarderPartyId?: string;
  rewardTypeId?: string;
  before?: string;
}

/** Only the filters that are set: an empty one must not narrow anything. */
function awardParams(a: EmpAwardsAdminArgs): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of [
    "status",
    "quarter",
    "locationId",
    "departmentId",
    "awarderPartyId",
    "rewardTypeId",
    "before",
  ] as const) {
    const v = a[k];
    if (v) out[k] = v;
  }
  return out;
}

export const employeeApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getEmployeeMe: build.query<EmpMe, void>({
      query: () => ({ url: "/employee/me" }),
      transformResponse: unwrap<EmpMe>,
      providesTags: [{ type: "EmployeeMe", id: "SELF" }],
      // The app's front door waits on this: one retry, not a ~14 s wait.
      extraOptions: { retryCondition: retryTransientOnce },
    }),
    getEmployeeMyProfile: build.query<EmpMyProfile | null, void>({
      query: () => ({ url: "/employee/me/profile" }),
      transformResponse: unwrap<EmpMyProfile | null>,
      providesTags: [{ type: "EmployeeMe", id: "PROFILE" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeMyScope: build.query<EmpMyScopeEntry[], void>({
      query: () => ({ url: "/employee/me/scope" }),
      transformResponse: unwrap<EmpMyScopeEntry[]>,
      providesTags: [{ type: "EmployeeMe", id: "SCOPE" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeMyScopePeople: build.query<EmpMyScopePeople, void>({
      query: () => ({ url: "/employee/me/scope/people" }),
      transformResponse: unwrap<EmpMyScopePeople>,
      providesTags: [{ type: "EmployeeMe", id: "SCOPE" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),

    // ── Settings ──
    getEmployeeSettings: build.query<EmpSettings, void>({
      query: () => ({ url: "/employee/settings" }),
      transformResponse: unwrap<EmpSettings>,
      providesTags: ["EmployeeSettings"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    updateEmployeeSettings: build.mutation<EmpSettings, EmployeeSettingsPatch>({
      query: (body) => ({ url: "/employee/admin/settings", method: "PUT", body }),
      transformResponse: unwrap<EmpSettings>,
      // Visibility and published fields change what the directory returns;
      // the My Apps choices change Home.
      invalidatesTags: ["EmployeeSettings", "EmployeeDirectory", "EmployeePerson", "EmployeeApps"],
    }),
    // Messaging & broadcasts: every messaging rule, section by section (Admins).
    // What a chat accepts (any member), so the composer can say so up front.
    getEmployeeMessagingLimits: build.query<ChatUploadLimits, void>({
      query: () => ({ url: "/employee/messaging-limits" }),
      transformResponse: unwrap<ChatUploadLimits>,
      providesTags: ["EmployeeMessagingSettings"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    /** The texting number and what Twilio says of it (refreshed when settings are saved). */
    getTextingNumber: build.query<TextingNumberStatus, void>({
      query: () => ({ url: "/employee/admin/texting-number" }),
      transformResponse: unwrap<TextingNumberStatus>,
      providesTags: ["EmployeeMessagingSettings"],
    }),
    getEmployeeMessagingSettings: build.query<
      Record<MessagingSectionKey, MessagingSectionState>,
      void
    >({
      query: () => ({ url: "/employee/admin/messaging-settings" }),
      transformResponse: unwrap<Record<MessagingSectionKey, MessagingSectionState>>,
      providesTags: ["EmployeeMessagingSettings"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    updateEmployeeMessagingSection: build.mutation<
      MessagingSectionState,
      { section: MessagingSectionKey; values: Record<string, number>; version: number }
    >({
      query: ({ section, values, version }) => ({
        url: `/employee/admin/messaging-settings/${encodeURIComponent(section)}`,
        method: "PUT",
        body: { values, version },
      }),
      transformResponse: unwrap<MessagingSectionState>,
      // Show what was saved straight away, from the save's own answer.
      async onQueryStarted({ section }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            employeeApi.util.updateQueryData("getEmployeeMessagingSettings", undefined, (all) => {
              all[section] = data;
            }),
          );
        } catch {
          // The screen shows the error; nothing to update.
        }
      },
      // The broadcast screens read these settings too (the composer's limits,
      // the channel, automations, keywords, templates, replies): they reload.
      invalidatesTags: (_result, _error, { section }) => [
        "EmployeeMessagingSettings",
        { type: "EmployeeBroadcasts", id: "SETUP" },
        { type: "EmployeeBroadcastAutomations", id: "SETUP" },
        "EmployeeBroadcastChannel",
        "EmployeeBroadcastKeywords",
        "EmployeeBroadcastTemplates",
        { type: "EmployeeBroadcastReplies", id: "INBOX" },
        // Messages on or off: the menu, posts and profiles follow at once.
        ...(section === "chats"
          ? [
              { type: "EmployeeMe" as const, id: "SELF" },
              { type: "EmployeeConversations" as const, id: "LIST" },
            ]
          : []),
      ],
    }),
    // Branding (P3-11): the logo; the name saves with the other settings.
    uploadEmployeeOrgLogo: build.mutation<void, File>({
      query: (file) => {
        const body = new FormData();
        body.append("logo", file);
        return { url: "/employee/admin/branding/logo", method: "PUT", body };
      },
      invalidatesTags: ["EmployeeSettings"],
    }),
    removeEmployeeOrgLogo: build.mutation<void, void>({
      query: () => ({ url: "/employee/admin/branding/logo", method: "DELETE" }),
      invalidatesTags: ["EmployeeSettings"],
    }),
    setUpEmployeePortal: build.mutation<{ portal: EmpPortalRef; created: boolean }, void>({
      query: () => ({ url: "/employee/admin/employee-portal", method: "POST" }),
      transformResponse: unwrap<{ portal: EmpPortalRef; created: boolean }>,
      invalidatesTags: ["EmployeeSettings"],
    }),

    // ── Directory ──
    getEmployeeDirectory: build.query<EmpDirectoryPage, EmployeeDirectoryParams>({
      query: (params) => ({ url: "/employee/directory", params }),
      transformResponse: unwrap<EmpDirectoryPage>,
      providesTags: ["EmployeeDirectory"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeDirectoryFilters: build.query<EmpDirectoryFilters, void>({
      query: () => ({ url: "/employee/directory/filters" }),
      transformResponse: unwrap<EmpDirectoryFilters>,
      providesTags: ["EmployeeDirectory"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    // The org chart: the caller's line and team, then a team at a time as
    // people are expanded (lazy). Reporting lines change with the directory.
    getEmployeeOrgView: build.query<EmpOrgView, void>({
      query: () => ({ url: "/employee/directory/org" }),
      transformResponse: unwrap<EmpOrgView>,
      providesTags: ["EmployeeDirectory"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeOrgPeople: build.query<
      EmpOrgPeople,
      Pick<EmployeeDirectoryParams, "q" | "locationId" | "departmentId">
    >({
      query: (params) => ({ url: "/employee/directory/org/people", params }),
      transformResponse: unwrap<EmpOrgPeople>,
      providesTags: ["EmployeeDirectory"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeOrgReports: build.query<EmpOrgPerson[], string>({
      query: (partyId) => ({
        url: `/employee/directory/org/${encodeURIComponent(partyId)}/reports`,
      }),
      transformResponse: unwrap<EmpOrgPerson[]>,
      providesTags: ["EmployeeDirectory"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeePerson: build.query<EmpPersonProfile, string>({
      query: (partyId) => ({ url: `/employee/people/${encodeURIComponent(partyId)}` }),
      transformResponse: unwrap<EmpPersonProfile>,
      providesTags: (_r, _e, partyId) => [{ type: "EmployeePerson", id: partyId }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),

    // ── My photo ──
    uploadEmployeePhoto: build.mutation<{ photoUrl: string }, File>({
      query: (file) => {
        const body = new FormData();
        body.append("photo", file);
        return { url: "/employee/me/photo", method: "PUT", body };
      },
      transformResponse: unwrap<{ photoUrl: string }>,
      invalidatesTags: [
        { type: "EmployeeMe", id: "PROFILE" },
        "EmployeeDirectory",
        "EmployeePerson",
      ],
    }),
    removeEmployeePhoto: build.mutation<void, void>({
      query: () => ({ url: "/employee/me/photo", method: "DELETE" }),
      invalidatesTags: [
        { type: "EmployeeMe", id: "PROFILE" },
        "EmployeeDirectory",
        "EmployeePerson",
      ],
    }),

    // ── Users & organisation (Admin) ──
    getEmployeeMembers: build.query<EmpMemberPage, EmployeeMemberListParams>({
      query: (params) => ({ url: "/employee/admin/members", params }),
      transformResponse: unwrap<EmpMemberPage>,
      providesTags: ["EmployeeMembers"],
    }),
    getEmployeeMember: build.query<EmpMemberSummary, string>({
      query: (partyId) => ({ url: `/employee/admin/members/${partyId}` }),
      transformResponse: unwrap<EmpMemberSummary>,
      providesTags: ["EmployeeMembers"],
    }),
    setEmployeeMemberStatus: build.mutation<
      EmpMemberSummary,
      { partyId: string; status: EmpMemberStatus }
    >({
      query: ({ partyId, status }) =>
        status === "deactivated"
          ? {
              url: `/employee/admin/members/${partyId}/deactivate`,
              method: "POST",
              body: { confirm: true },
            }
          : { url: `/employee/admin/members/${partyId}/reactivate`, method: "POST" },
      transformResponse: unwrap<EmpMemberSummary>,
      // The person leaves (or rejoins) the directory.
      invalidatesTags: (_r, _e, { partyId }) => [
        "EmployeeMembers",
        "EmployeeLocations",
        "EmployeeDirectory",
        { type: "EmployeePerson", id: partyId },
      ],
    }),
    getEmployeePersonContact: build.query<EmpPersonContact, string>({
      query: (partyId) => ({ url: `/employee/admin/people/${partyId}/contact` }),
      transformResponse: unwrap<EmpPersonContact>,
      providesTags: (_r, _e, partyId) => [{ type: "EmployeeBroadcastContacts", id: partyId }],
    }),
    getEmployeeMemberScope: build.query<EmpScopeEntryView[], string>({
      query: (partyId) => ({ url: `/employee/admin/members/${partyId}/scope` }),
      transformResponse: unwrap<EmpScopeEntryView[]>,
      providesTags: (_r, _e, partyId) => [{ type: "EmployeeScope", id: partyId }],
    }),
    replaceEmployeeMemberScope: build.mutation<
      EmpScopeEntryView[],
      { partyId: string; entries: EmpScopeEntry[] }
    >({
      query: ({ partyId, entries }) => ({
        url: `/employee/admin/members/${partyId}/scope`,
        method: "PUT",
        body: { entries },
      }),
      transformResponse: unwrap<EmpScopeEntryView[]>,
      invalidatesTags: (_r, _e, { partyId }) => [
        { type: "EmployeeScope", id: partyId },
        "EmployeeMembers",
        "EmployeeLocations",
        // An admin editing their own scope (or testing as a manager) sees it at once.
        { type: "EmployeeMe", id: "SELF" },
        { type: "EmployeeMe", id: "SCOPE" },
      ],
    }),
    getEmployeeLocations: build.query<EmpLocationsOverview, void>({
      query: () => ({ url: "/employee/admin/locations" }),
      transformResponse: unwrap<EmpLocationsOverview>,
      providesTags: ["EmployeeLocations"],
    }),
    createEmployeeLocation: build.mutation<EmployeeLocationRecord, { name: string }>({
      query: (body) => ({ url: "/employee/admin/locations", method: "POST", body }),
      transformResponse: unwrap<EmployeeLocationRecord>,
      invalidatesTags: ["EmployeeLocations"],
    }),
    updateEmployeeLocation: build.mutation<
      EmployeeLocationRecord,
      { locationId: string; name?: string; status?: "active" | "archived" }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/employee/admin/locations/${locationId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: unwrap<EmployeeLocationRecord>,
      // Directory rows, its location filter and profiles show the location's name.
      invalidatesTags: [
        "EmployeeLocations",
        "EmployeeMembers",
        { type: "EmployeeMe", id: "SELF" },
        "EmployeeDirectory",
        "EmployeePerson",
      ],
    }),
    setEmployeePersonLocation: build.mutation<
      EmpMemberSummary,
      // null takes them out of their location.
      { partyId: string; locationId: string | null }
    >({
      query: ({ partyId, locationId }) => ({
        url: `/employee/admin/people/${partyId}/location`,
        method: "PUT",
        body: { locationId },
      }),
      transformResponse: unwrap<EmpMemberSummary>,
      // Their directory row and profile show the new location.
      invalidatesTags: (_r, _e, { partyId }) => [
        "EmployeeMembers",
        "EmployeeLocations",
        "EmployeeDirectory",
        { type: "EmployeePerson", id: partyId },
      ],
    }),

    // The bell. The live push (websocketMiddleware) refetches LIST and COUNT;
    // the count also comes from the database on every load (roadmap decision 16).
    getEmployeeNotifications: build.query<EmpNotificationPage, { before?: string } | void>({
      query: (args) => ({
        url: "/employee/notifications",
        params: args && args.before ? { before: args.before } : undefined,
      }),
      transformResponse: unwrap<EmpNotificationPage>,
      providesTags: [{ type: "EmployeeNotifications", id: "LIST" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeUnreadCount: build.query<number, void>({
      query: () => ({ url: "/employee/notifications/unread-count" }),
      transformResponse: (res: { data: { count: number } }) => res.data.count,
      providesTags: [{ type: "EmployeeNotifications", id: "COUNT" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    markEmployeeNotificationsRead: build.mutation<void, string[]>({
      query: (ids) => ({ url: "/employee/notifications/read", method: "POST", body: { ids } }),
      invalidatesTags: [
        { type: "EmployeeNotifications", id: "LIST" },
        { type: "EmployeeNotifications", id: "COUNT" },
      ],
    }),
    markAllEmployeeNotificationsRead: build.mutation<void, void>({
      query: () => ({ url: "/employee/notifications/read-all", method: "POST" }),
      invalidatesTags: [
        { type: "EmployeeNotifications", id: "LIST" },
        { type: "EmployeeNotifications", id: "COUNT" },
      ],
    }),
    // Header search: each group follows its own page's visibility on the server.
    searchEmployee: build.query<EmpSearchResults, string>({
      query: (q) => ({ url: "/employee/search", params: { q } }),
      transformResponse: unwrap<EmpSearchResults>,
      providesTags: ["EmployeeDirectory"],
      keepUnusedDataFor: 30,
      extraOptions: { retryCondition: retryTransientOnly },
    }),

    // ── My Apps (P3-10) ──
    getEmployeeMyApps: build.query<EmpMyApps, void>({
      query: () => ({ url: "/employee/apps" }),
      transformResponse: unwrap<EmpMyApps>,
      providesTags: ["EmployeeApps"],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeMyAppsAdmin: build.query<EmpMyAppsAdmin, void>({
      query: () => ({ url: "/employee/admin/my-apps" }),
      transformResponse: unwrap<EmpMyAppsAdmin>,
      providesTags: ["EmployeeApps"],
    }),
    // External apps (settings.manage): sent as multipart, the app as JSON in
    // "app" and an optional "logo" image file.
    saveEmployeeExternalApp: build.mutation<
      EmpExternalApp,
      { id?: string; app: EmpExternalAppInput; logo?: File | null }
    >({
      query: ({ id, app, logo }) => {
        const body = new FormData();
        body.append("app", JSON.stringify(app));
        if (logo) body.append("logo", logo);
        return id
          ? { url: `/employee/admin/external-apps/${id}`, method: "PUT", body }
          : { url: "/employee/admin/external-apps", method: "POST", body };
      },
      transformResponse: unwrap<EmpExternalApp>,
      invalidatesTags: ["EmployeeApps"],
    }),
    deleteEmployeeExternalApp: build.mutation<void, string>({
      query: (id) => ({ url: `/employee/admin/external-apps/${id}`, method: "DELETE" }),
      invalidatesTags: ["EmployeeApps"],
    }),

    // My birthday (P4-5): self-scoped.
    getEmployeeMyBirthday: build.query<EmpMyBirthday, void>({
      query: () => ({ url: "/employee/me/birthday" }),
      transformResponse: unwrap<EmpMyBirthday>,
      providesTags: [{ type: "EmployeeMe", id: "BIRTHDAY" }],
    }),
    setEmployeeMyBirthday: build.mutation<EmpMyBirthday, EmpMyBirthday>({
      query: (body) => ({ url: "/employee/me/birthday", method: "PUT", body }),
      transformResponse: unwrap<EmpMyBirthday>,
      invalidatesTags: [{ type: "EmployeeMe", id: "BIRTHDAY" }],
    }),

    // ── Surveys (Phase 7, #1204) ──
    // Reading the list needs surveys.results, so a Manager can open the page;
    // every write needs surveys.build, which is Admin-only.
    getEmployeeSurveys: build.query<EmpSurvey[], void>({
      query: () => ({ url: "/employee/surveys" }),
      transformResponse: unwrap<EmpSurvey[]>,
      providesTags: [{ type: "EmployeeSurveys", id: "LIST" }],
    }),
    getEmployeeSurvey: build.query<EmpSurveyDetail, string>({
      query: (surveyId) => ({ url: `/employee/surveys/${surveyId}` }),
      transformResponse: unwrap<EmpSurveyDetail>,
      providesTags: (_r, _e, id) => [{ type: "EmployeeSurveys", id }],
    }),

    createEmployeeSurvey: build.mutation<EmpSurvey, EmpSurveyInput>({
      query: (body) => ({ url: "/employee/surveys", method: "POST", body }),
      transformResponse: unwrap<EmpSurvey>,
      invalidatesTags: [{ type: "EmployeeSurveys", id: "LIST" }],
    }),
    // A draft's details: only what changed is sent.
    updateEmployeeSurvey: build.mutation<
      EmpSurvey,
      { surveyId: string; survey: Partial<EmpSurveyInput> }
    >({
      query: ({ surveyId, survey }) => ({
        url: `/employee/surveys/${surveyId}`,
        method: "PUT",
        body: survey,
      }),
      transformResponse: unwrap<EmpSurvey>,
      invalidatesTags: (_r, _e, { surveyId }) => [
        { type: "EmployeeSurveys", id: surveyId },
        { type: "EmployeeSurveys", id: "LIST" },
      ],
    }),
    addEmployeeSurveyQuestion: build.mutation<
      EmpSurveyDetail,
      { surveyId: string; question: EmpSurveyQuestionInput }
    >({
      query: ({ surveyId, question }) => ({
        url: `/employee/surveys/${surveyId}/questions`,
        method: "POST",
        body: question,
      }),
      transformResponse: unwrap<EmpSurveyDetail>,
      invalidatesTags: (_r, _e, { surveyId }) => [
        { type: "EmployeeSurveys", id: surveyId },
        { type: "EmployeeSurveys", id: "LIST" },
      ],
    }),
    updateEmployeeSurveyQuestion: build.mutation<
      EmpSurveyDetail,
      { surveyId: string; questionId: string; question: EmpSurveyQuestionInput }
    >({
      query: ({ surveyId, questionId, question }) => ({
        url: `/employee/surveys/${surveyId}/questions/${questionId}`,
        method: "PUT",
        body: question,
      }),
      transformResponse: unwrap<EmpSurveyDetail>,
      invalidatesTags: (_r, _e, { surveyId }) => [
        { type: "EmployeeSurveys", id: surveyId },
        { type: "EmployeeSurveys", id: "LIST" },
      ],
    }),
    reorderEmployeeSurveyQuestions: build.mutation<
      EmpSurveyDetail,
      { surveyId: string; questionIds: string[] }
    >({
      query: ({ surveyId, questionIds }) => ({
        url: `/employee/surveys/${surveyId}/questions/order`,
        method: "PUT",
        body: { questionIds },
      }),
      transformResponse: unwrap<EmpSurveyDetail>,
      invalidatesTags: (_r, _e, { surveyId }) => [
        { type: "EmployeeSurveys", id: surveyId },
        { type: "EmployeeSurveys", id: "LIST" },
      ],
    }),
    deleteEmployeeSurveyQuestion: build.mutation<
      EmpSurveyDetail,
      { surveyId: string; questionId: string }
    >({
      query: ({ surveyId, questionId }) => ({
        url: `/employee/surveys/${surveyId}/questions/${questionId}`,
        method: "DELETE",
      }),
      transformResponse: unwrap<EmpSurveyDetail>,
      // The list shows how many questions each survey has.
      invalidatesTags: (_r, _e, { surveyId }) => [
        { type: "EmployeeSurveys", id: surveyId },
        { type: "EmployeeSurveys", id: "LIST" },
      ],
    }),
    scheduleEmployeeSurvey: build.mutation<EmpSurvey, string>({
      query: (surveyId) => ({ url: `/employee/surveys/${surveyId}/schedule`, method: "POST" }),
      transformResponse: unwrap<EmpSurvey>,
      invalidatesTags: [{ type: "EmployeeSurveys", id: "LIST" }],
    }),
    unscheduleEmployeeSurvey: build.mutation<EmpSurvey, string>({
      query: (surveyId) => ({ url: `/employee/surveys/${surveyId}/unschedule`, method: "POST" }),
      transformResponse: unwrap<EmpSurvey>,
      invalidatesTags: (_r, _e, surveyId) => [
        { type: "EmployeeSurveys", id: surveyId },
        { type: "EmployeeSurveys", id: "LIST" },
      ],
    }),
    closeEmployeeSurvey: build.mutation<EmpSurvey, string>({
      query: (surveyId) => ({ url: `/employee/surveys/${surveyId}/close`, method: "POST" }),
      transformResponse: unwrap<EmpSurvey>,
      invalidatesTags: [{ type: "EmployeeSurveys", id: "LIST" }],
    }),
    deleteEmployeeSurvey: build.mutation<void, string>({
      query: (surveyId) => ({ url: `/employee/surveys/${surveyId}`, method: "DELETE" }),
      invalidatesTags: [{ type: "EmployeeSurveys", id: "LIST" }],
    }),
    getEmployeeSurveyResults: build.query<EmpSurveyResults, string>({
      query: (surveyId) => ({ url: `/employee/surveys/${surveyId}/results` }),
      transformResponse: unwrap<EmpSurveyResults>,
      providesTags: (_r, _e, surveyId) => [{ type: "EmployeeSurveys", id: surveyId }],
    }),
    shareEmployeeSurveyResults: build.mutation<
      EmpFeedPost,
      { surveyId: string; runId: string } & EmpShareSurveyResultsInput
    >({
      query: ({ surveyId, runId, ...body }) => ({
        url: `/employee/surveys/${surveyId}/runs/${runId}/share`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap<EmpFeedPost>,
      // The round now says it's shared; the feed and Manage → Content gain the post.
      invalidatesTags: (_r, _e, { surveyId }) => [
        { type: "EmployeeSurveys", id: surveyId },
        ...BOTH_FEEDS,
        "EmployeeContent",
      ],
    }),
    exportEmployeeSurveyResults: build.mutation<string, string>({
      // Text, not JSON: the server builds the CSV from the same query the
      // screen reads, so the threshold cannot hold on screen and leak here.
      query: (surveyId) => ({
        url: `/employee/surveys/${surveyId}/results.csv`,
        responseHandler: (response: Response) => response.text(),
      }),
    }),

    // ── Answering a survey, and the due list (#1214) ──
    getEmployeeDue: build.query<EmpDueItem[], void>({
      query: () => ({ url: "/employee/me/due" }),
      transformResponse: unwrap<EmpDueItem[]>,
      // Acknowledging, or a new version or requirement in Resources, changes
      // it too: every such mutation invalidates the acknowledgement lists.
      providesTags: [
        { type: "EmployeeDue", id: "LIST" },
        { type: "EmployeeLibrary", id: "ACKS" },
      ],
    }),
    // The surveys I'm done with; submitting or skipping one changes it.
    getEmployeeSurveyHistory: build.query<EmpSurveyHistoryItem[], void>({
      query: () => ({ url: "/employee/me/surveys/history" }),
      transformResponse: unwrap<EmpSurveyHistoryItem[]>,
      providesTags: [{ type: "EmployeeDue", id: "LIST" }],
    }),
    getEmployeeSurveyToAnswer: build.query<EmpSurveyToAnswer, string>({
      query: (runId) => ({ url: `/employee/me/surveys/${runId}` }),
      transformResponse: unwrap<EmpSurveyToAnswer>,
      providesTags: (_r, _e, runId) => [{ type: "EmployeeDue", id: runId }],
    }),
    saveEmployeeSurveyProgress: build.mutation<
      { saved: number },
      { runId: string; answers: Record<string, EmpSurveyAnswer> }
    >({
      query: ({ runId, answers }) => ({
        url: `/employee/me/surveys/${runId}/progress`,
        method: "PUT",
        body: { answers },
      }),
      transformResponse: unwrap<{ saved: number }>,
      // Deliberately invalidates nothing: a save happens as you move between
      // questions, and refetching the survey mid-answer would be a jolt.
    }),
    submitEmployeeSurvey: build.mutation<
      { submitted: true },
      { runId: string; answers: Record<string, EmpSurveyAnswer> }
    >({
      query: ({ runId, answers }) => ({
        url: `/employee/me/surveys/${runId}/submit`,
        method: "POST",
        body: { answers },
      }),
      transformResponse: unwrap<{ submitted: true }>,
      invalidatesTags: [{ type: "EmployeeDue", id: "LIST" }],
    }),
    optOutOfEmployeeSurvey: build.mutation<{ optedOut: true }, string>({
      query: (runId) => ({ url: `/employee/me/surveys/${runId}/opt-out`, method: "POST" }),
      transformResponse: unwrap<{ optedOut: true }>,
      invalidatesTags: [{ type: "EmployeeDue", id: "LIST" }],
    }),

    // ── Messages (Phase 8, #1204) ──
    getEmployeeConversations: build.query<
      EmpConversation[],
      { archived?: boolean; q?: string } | void
    >({
      query: (args) => ({
        url: "/employee/conversations",
        params: {
          ...(args?.archived ? { archived: "true" } : {}),
          ...(args?.q ? { q: args.q } : {}),
        },
      }),
      transformResponse: unwrap<EmpConversation[]>,
      providesTags: [{ type: "EmployeeConversations", id: "LIST" }],
      // A 4xx (Messages switched off, not in the directory) is an answer: not retried.
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    /**
     * The badge on the Messages menu item (#1346).
     *
     * Tagged with the conversations LIST, so every socket event and every
     * mutation that already invalidates it — a message arriving, a thread
     * read, archived, cleared or deleted — refreshes the badge too. Nothing
     * here polls: the number tracks the list it stands for because it is
     * invalidated by the same things.
     */
    getEmployeeUnreadMessages: build.query<{ count: number }, void>({
      query: () => ({ url: "/employee/conversations/unread" }),
      transformResponse: unwrap<{ count: number }>,
      providesTags: [{ type: "EmployeeConversations", id: "LIST" }],
      // A 4xx (Messages switched off, not in the directory) is an answer: not retried.
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeConversation: build.query<EmpConversation, string>({
      query: (id) => ({ url: `/employee/conversations/${id}` }),
      transformResponse: unwrap<EmpConversation>,
      providesTags: (_r, _e, id) => [{ type: "EmployeeConversations", id }],
      // A 4xx (Messages switched off, not in the directory) is an answer: not retried.
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    openEmployeeConversation: build.mutation<EmpConversation, string>({
      query: (partyId) => ({ url: "/employee/conversations", method: "POST", body: { partyId } }),
      transformResponse: unwrap<EmpConversation>,
      invalidatesTags: [{ type: "EmployeeConversations", id: "LIST" }],
    }),
    getEmployeeMessages: build.query<
      { items: EmpMessage[]; nextCursor: string | null },
      { conversationId: string; before?: string; after?: string }
    >({
      query: ({ conversationId, before, after }) => ({
        url: `/employee/conversations/${conversationId}/messages`,
        params: { ...(before ? { before } : {}), ...(after ? { after } : {}) },
      }),
      transformResponse: unwrap<{ items: EmpMessage[]; nextCursor: string | null }>,
      providesTags: (_r, _e, { conversationId }) => [
        { type: "EmployeeMessages", id: conversationId },
      ],
      // A 4xx (Messages switched off, not in the directory) is an answer: not retried.
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    sendEmployeeMessage: build.mutation<
      EmpMessage,
      {
        conversationId: string;
        body: string;
        clientId: string;
        attachmentIds?: string[];
        /** Share a post into this conversation (direct conversations only). */
        sharedPostId?: string;
      }
    >({
      query: ({ conversationId, ...body }) => ({
        url: `/employee/conversations/${conversationId}/messages`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap<EmpMessage>,
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: "EmployeeMessages", id: conversationId },
        { type: "EmployeeConversations", id: "LIST" },
      ],
    }),
    editEmployeeMessage: build.mutation<
      EmpMessage,
      { conversationId: string; messageId: string; body: string }
    >({
      query: ({ conversationId, messageId, body }) => ({
        url: `/employee/conversations/${conversationId}/messages/${messageId}`,
        method: "PATCH",
        body: { body },
      }),
      transformResponse: unwrap<EmpMessage>,
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: "EmployeeMessages", id: conversationId },
        { type: "EmployeeConversations", id: "LIST" },
      ],
    }),
    deleteEmployeeMessage: build.mutation<void, { conversationId: string; messageId: string }>({
      query: ({ conversationId, messageId }) => ({
        url: `/employee/conversations/${conversationId}/messages/${messageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: "EmployeeMessages", id: conversationId },
        { type: "EmployeeConversations", id: "LIST" },
      ],
    }),
    deleteEmployeeConversation: build.mutation<void, string>({
      query: (conversationId) => ({
        url: `/employee/conversations/${conversationId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "EmployeeConversations", id: "LIST" }],
    }),
    markEmployeeConversationRead: build.mutation<{ lastReadAt: string }, string>({
      query: (conversationId) => ({
        url: `/employee/conversations/${conversationId}/read`,
        method: "POST",
      }),
      transformResponse: unwrap<{ lastReadAt: string }>,
      invalidatesTags: [{ type: "EmployeeConversations", id: "LIST" }],
    }),
    sendEmployeeTyping: build.mutation<void, string>({
      // Fire and forget: nothing is stored, and a missed one simply doesn't show.
      query: (conversationId) => ({
        url: `/employee/conversations/${conversationId}/typing`,
        method: "POST",
      }),
    }),
    setEmployeeConversationFlag: build.mutation<
      EmpConversation,
      {
        conversationId: string;
        flag: "pinned" | "archived" | "muted";
        on: boolean;
        mutedUntil?: string;
      }
    >({
      query: ({ conversationId, ...body }) => ({
        url: `/employee/conversations/${conversationId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: unwrap<EmpConversation>,
      invalidatesTags: [{ type: "EmployeeConversations", id: "LIST" }],
    }),
    uploadEmployeeAttachment: build.mutation<EmpAttachment, { conversationId: string; file: File }>(
      {
        query: ({ conversationId, file }) => {
          const body = new FormData();
          body.append("file", file);
          return {
            url: `/employee/conversations/${conversationId}/attachments`,
            method: "POST",
            body,
          };
        },
        transformResponse: unwrap<EmpAttachment>,
      },
    ),
    uploadEmployeeVoiceNote: build.mutation<
      EmpAttachment,
      { conversationId: string; blob: Blob; durationMs: number; waveform: number[] }
    >({
      query: ({ conversationId, blob, durationMs, waveform }) => {
        const body = new FormData();
        body.append("audio", blob, "voice.mp3");
        body.append("durationMs", String(Math.round(durationMs)));
        body.append("waveform", JSON.stringify(waveform));
        return { url: `/employee/conversations/${conversationId}/voice`, method: "POST", body };
      },
      transformResponse: unwrap<EmpAttachment>,
    }),
    reportEmployeeMessage: build.mutation<
      void,
      { conversationId: string; messageId: string; reason: string; details?: string }
    >({
      query: ({ conversationId, messageId, ...body }) => ({
        url: `/employee/conversations/${conversationId}/messages/${messageId}/report`,
        method: "POST",
        body,
      }),
    }),
    getEmployeeMessageReports: build.query<EmpMessageReport[], { status?: string } | void>({
      query: (args) => ({
        url: "/employee/admin/message-reports",
        params: args?.status ? { status: args.status } : undefined,
      }),
      transformResponse: unwrap<EmpMessageReport[]>,
      providesTags: [{ type: "EmployeeMessageReports", id: "LIST" }],
    }),
    // The Reports badge: counted on the server, never the list of copies.
    getEmployeeOpenMessageReportCount: build.query<number, void>({
      query: () => ({ url: "/employee/admin/message-reports/count" }),
      transformResponse: (res: { data: { count: number } }) => res.data.count,
      providesTags: [{ type: "EmployeeMessageReports", id: "LIST" }],
    }),
    reviewEmployeeMessageReport: build.mutation<
      void,
      { reportId: string; status: "actioned" | "dismissed" }
    >({
      query: ({ reportId, status }) => ({
        url: `/employee/admin/message-reports/${reportId}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: [{ type: "EmployeeMessageReports", id: "LIST" }],
    }),

    // ── Recognition catalogue (Phase 4) ──
    getEmployeeBadges: build.query<EmpBadge[], void>({
      query: () => ({ url: "/employee/recognition/badges" }),
      transformResponse: unwrap<EmpBadge[]>,
      providesTags: [{ type: "EmployeeRecognition", id: "BADGES" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeEcardDesigns: build.query<EmpEcardDesign[], void>({
      query: () => ({ url: "/employee/recognition/designs" }),
      transformResponse: unwrap<EmpEcardDesign[]>,
      providesTags: [{ type: "EmployeeRecognition", id: "DESIGNS" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeBadgesAdmin: build.query<EmpBadgeAdmin[], void>({
      query: () => ({ url: "/employee/admin/recognition/badges" }),
      transformResponse: unwrap<EmpBadgeAdmin[]>,
      providesTags: [{ type: "EmployeeRecognition", id: "BADGES" }],
    }),
    getEmployeeEcardDesignsAdmin: build.query<EmpEcardDesignAdmin[], void>({
      query: () => ({ url: "/employee/admin/recognition/designs" }),
      transformResponse: unwrap<EmpEcardDesignAdmin[]>,
      providesTags: [{ type: "EmployeeRecognition", id: "DESIGNS" }],
    }),
    saveEmployeeBadge: build.mutation<EmpBadge, { id?: string; badge: EmpBadgeInput }>({
      query: ({ id, badge }) => ({
        url: id ? `/employee/admin/recognition/badges/${id}` : "/employee/admin/recognition/badges",
        method: id ? "PUT" : "POST",
        body: badge,
      }),
      transformResponse: unwrap<EmpBadge>,
      invalidatesTags: [{ type: "EmployeeRecognition", id: "BADGES" }],
    }),
    saveEmployeeEcardDesign: build.mutation<
      EmpEcardDesign,
      { id?: string; design: EmpEcardDesignInput }
    >({
      query: ({ id, design }) => ({
        url: id
          ? `/employee/admin/recognition/designs/${id}`
          : "/employee/admin/recognition/designs",
        method: id ? "PUT" : "POST",
        body: design,
      }),
      transformResponse: unwrap<EmpEcardDesign>,
      invalidatesTags: [{ type: "EmployeeRecognition", id: "DESIGNS" }],
    }),
    setEmployeeRecognitionRetired: build.mutation<
      void,
      { kind: "badges" | "designs"; id: string; retired: boolean }
    >({
      query: ({ kind, id, retired }) => ({
        url: `/employee/admin/recognition/${kind}/${id}/retire`,
        method: "POST",
        body: { retired },
      }),
      invalidatesTags: (_r, _e, { kind }) => [
        { type: "EmployeeRecognition", id: kind === "badges" ? "BADGES" : "DESIGNS" },
      ],
    }),
    deleteEmployeeRecognitionItem: build.mutation<void, { kind: "badges" | "designs"; id: string }>(
      {
        query: ({ kind, id }) => ({
          url: `/employee/admin/recognition/${kind}/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: (_r, _e, { kind }) => [
          { type: "EmployeeRecognition", id: kind === "badges" ? "BADGES" : "DESIGNS" },
        ],
      },
    ),
    reorderEmployeeRecognition: build.mutation<void, { kind: "badges" | "designs"; ids: string[] }>(
      {
        query: ({ kind, ids }) => ({
          url: `/employee/admin/recognition/${kind}/order`,
          method: "PUT",
          body: { ids },
        }),
        invalidatesTags: (_r, _e, { kind }) => [
          { type: "EmployeeRecognition", id: kind === "badges" ? "BADGES" : "DESIGNS" },
        ],
      },
    ),

    // ── Gift cards (Phase 5): Administration → Gift cards ──
    getEmployeeRewardTypesAdmin: build.query<EmpRewardTypeAdmin[], void>({
      query: () => ({ url: "/employee/admin/giftcards/types" }),
      transformResponse: unwrap<EmpRewardTypeAdmin[]>,
      providesTags: [{ type: "EmployeeGiftcards", id: "TYPES" }],
    }),
    saveEmployeeRewardType: build.mutation<
      EmpRewardTypeAdmin,
      { id?: string; type: EmpRewardTypeInput }
    >({
      query: ({ id, type }) => ({
        url: id ? `/employee/admin/giftcards/types/${id}` : "/employee/admin/giftcards/types",
        method: id ? "PUT" : "POST",
        body: type,
      }),
      transformResponse: unwrap<EmpRewardTypeAdmin>,
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    setEmployeeRewardTypeRetired: build.mutation<void, { id: string; retired: boolean }>({
      query: ({ id, retired }) => ({
        url: `/employee/admin/giftcards/types/${id}/retire`,
        method: "POST",
        body: { retired },
      }),
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    deleteEmployeeRewardType: build.mutation<void, string>({
      query: (id) => ({ url: `/employee/admin/giftcards/types/${id}`, method: "DELETE" }),
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    reorderEmployeeRewardTypes: build.mutation<void, string[]>({
      query: (ids) => ({
        url: "/employee/admin/giftcards/types/order",
        method: "PUT",
        body: { ids },
      }),
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    getEmployeeGiftCodes: build.query<
      EmpGiftCodePage,
      { typeId?: string; status?: EmpGiftCodeFilter; before?: string }
    >({
      query: ({ typeId, status, before }) => ({
        url: "/employee/admin/giftcards/codes",
        params: {
          ...(typeId ? { typeId } : {}),
          ...(status ? { status } : {}),
          ...(before ? { before } : {}),
        },
      }),
      transformResponse: unwrap<EmpGiftCodePage>,
      providesTags: (_r, _e, { before }) =>
        before ? [] : [{ type: "EmployeeGiftcards", id: "CODES" }],
    }),
    // The body holds codes. RTK Query keeps a mutation's arguments in memory
    // (never persisted: the api slice is blacklisted) until it is reset, so the
    // Load codes dialog resets it as soon as the load finishes.
    loadEmployeeGiftCodes: build.mutation<
      EmpCodeLoadResult,
      { typeId: string; load: EmpCodeLoadInput }
    >({
      query: ({ typeId, load }) => ({
        url: `/employee/admin/giftcards/types/${typeId}/codes`,
        method: "POST",
        body: load,
      }),
      transformResponse: unwrap<EmpCodeLoadResult>,
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    voidEmployeeGiftCodes: build.mutation<
      { voided: number },
      { codeIds: string[]; reason: string }
    >({
      query: (body) => ({ url: "/employee/admin/giftcards/codes/void", method: "POST", body }),
      transformResponse: unwrap<{ voided: number }>,
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    getEmployeeGiftSettings: build.query<EmpGiftSettings, void>({
      query: () => ({ url: "/employee/admin/giftcards/settings" }),
      transformResponse: unwrap<EmpGiftSettings>,
      providesTags: [{ type: "EmployeeGiftcards", id: "SETTINGS" }],
    }),
    updateEmployeeGiftSettings: build.mutation<EmpGiftSettings, Partial<EmpGiftSettings>>({
      query: (body) => ({ url: "/employee/admin/giftcards/settings", method: "PUT", body }),
      transformResponse: unwrap<EmpGiftSettings>,
      // The default allowance shows on every Manager without their own.
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    // ── Manage → Rewards → Give (Managers and Admins) ──
    getEmployeeAwardableTypes: build.query<EmpAwardableType[], void>({
      query: () => ({ url: "/employee/rewards/give/types" }),
      transformResponse: unwrap<EmpAwardableType[]>,
      providesTags: [{ type: "EmployeeGiftcards", id: "GIVE" }],
    }),
    getEmployeeMyAllowance: build.query<EmpMyAllowance, void>({
      query: () => ({ url: "/employee/rewards/give/allowance" }),
      transformResponse: unwrap<EmpMyAllowance>,
      providesTags: [{ type: "EmployeeGiftcards", id: "GIVE" }],
    }),
    getEmployeeAwardsGiven: build.query<EmpAwardGivenPage, { before?: string } | void>({
      query: (arg) => ({
        url: "/employee/rewards/give/awards",
        params: arg?.before ? { before: arg.before } : {},
      }),
      transformResponse: unwrap<EmpAwardGivenPage>,
      providesTags: (_r, _e, arg) =>
        arg?.before ? [] : [{ type: "EmployeeGiftcards", id: "GIVEN" }],
    }),
    giveEmployeeAward: build.mutation<EmpAwardResult, EmpAwardInput>({
      query: (body) => ({ url: "/employee/rewards/give/awards", method: "POST", body }),
      transformResponse: unwrap<EmpAwardResult>,
      // Stock, allowances and budgets all move with an award; a shout-out posted
      // with it shows in the feed.
      invalidatesTags: (result) => [
        ...GIFTCARDS_CHANGED,
        // A reward given with a shout-out is recognition too.
        ...(result?.postId ? [...BOTH_FEEDS, RECEIVED] : []),
      ],
    }),
    // ── My gift cards and rewards (everyone) ──
    getEmployeeMyRewards: build.query<EmpMyReward[], void>({
      query: () => ({ url: "/employee/rewards/mine" }),
      transformResponse: unwrap<EmpMyReward[]>,
      providesTags: [{ type: "EmployeeGiftcards", id: "MINE" }],
    }),
    // The result is a code. RTK Query keeps a mutation's result in memory
    // (never persisted: the api slice is blacklisted) until it is reset, so
    // the reveal dialog resets it as soon as it closes.
    revealEmployeeCode: build.mutation<EmpRevealedCode, string>({
      query: (awardId) => ({
        url: `/employee/rewards/mine/${awardId}/reveal`,
        method: "POST",
        body: { acknowledgedWarning: true },
      }),
      transformResponse: unwrap<EmpRevealedCode>,
      invalidatesTags: [{ type: "EmployeeGiftcards", id: "MINE" }],
    }),

    // ── Fulfilment queue (Managers and Admins) ──
    getEmployeeFulfilment: build.query<EmpFulfilmentItem[], { view: "open" | "done" }>({
      query: ({ view }) => ({ url: "/employee/rewards/fulfilment", params: { view } }),
      transformResponse: unwrap<EmpFulfilmentItem[]>,
      providesTags: [{ type: "EmployeeGiftcards", id: "FULFILMENT" }],
    }),
    advanceEmployeeFulfilment: build.mutation<
      { status: string },
      { awardId: string; step: EmpFulfilmentStep; note?: string | null }
    >({
      query: ({ awardId, ...body }) => ({
        url: `/employee/rewards/fulfilment/${awardId}`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap<{ status: string }>,
      invalidatesTags: GIFTCARDS_CHANGED,
    }),

    // ── Overview, awards and audit (Admins) ──
    getEmployeeGiftOverview: build.query<EmpGiftOverview, void>({
      query: () => ({ url: "/employee/admin/giftcards/overview" }),
      transformResponse: unwrap<EmpGiftOverview>,
      providesTags: [{ type: "EmployeeGiftcards", id: "OVERVIEW" }],
    }),
    getEmployeeAwardsAdmin: build.query<EmpAwardAdminPage, EmpAwardsAdminArgs>({
      query: (args) => ({
        url: "/employee/admin/giftcards/awards",
        params: awardParams(args),
      }),
      transformResponse: unwrap<EmpAwardAdminPage>,
      providesTags: (_r, _e, { before }) =>
        before ? [] : [{ type: "EmployeeGiftcards", id: "AWARDS" }],
    }),
    /**
     * What the awards matching the same filters add up to (#1369 AC7). Shares
     * the AWARDS tag, so approving or cancelling an award corrects the totals
     * as well as the rows.
     */
    getEmployeeAwardTotals: build.query<EmpAwardTotals, EmpAwardsAdminArgs>({
      query: (args) => ({
        url: "/employee/admin/giftcards/awards/totals",
        params: awardParams({ ...args, before: undefined }),
      }),
      transformResponse: unwrap<EmpAwardTotals>,
      providesTags: [{ type: "EmployeeGiftcards", id: "AWARDS" }],
    }),
    getEmployeeGiftAudit: build.query<EmpGiftAuditPage, { before?: string } | void>({
      query: (arg) => ({
        url: "/employee/admin/giftcards/audit",
        params: arg?.before ? { before: arg.before } : {},
      }),
      transformResponse: unwrap<EmpGiftAuditPage>,
      providesTags: (_r, _e, arg) =>
        arg?.before ? [] : [{ type: "EmployeeGiftcards", id: "AUDIT" }],
    }),
    cancelEmployeeAward: build.mutation<void, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/employee/admin/giftcards/awards/${id}/cancel`,
        method: "POST",
        body: { reason },
      }),
      // A cancelled award returns its code or stock and stops counting.
      invalidatesTags: GIFTCARDS_CHANGED,
    }),

    // ── Resource library (Admins) ──
    getEmployeeLibraryCategories: build.query<EmpResourceCategory[], void>({
      query: () => ({ url: "/employee/admin/library/categories" }),
      transformResponse: unwrap<EmpResourceCategory[]>,
      providesTags: [{ type: "EmployeeLibrary", id: "CATEGORIES" }],
    }),
    saveEmployeeLibraryCategory: build.mutation<
      EmpResourceCategory,
      { id?: string; category: EmpResourceCategoryInput }
    >({
      query: ({ id, category }) =>
        id
          ? { url: `/employee/admin/library/categories/${id}`, method: "PUT", body: category }
          : { url: "/employee/admin/library/categories", method: "POST", body: category },
      transformResponse: unwrap<EmpResourceCategory>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "CATEGORIES" },
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
        { type: "EmployeeLibrary", id: "ACKS" },
        { type: "EmployeeLibrary", id: "REVIEW" },
      ],
    }),
    deleteEmployeeLibraryCategory: build.mutation<void, string>({
      query: (id) => ({ url: `/employee/admin/library/categories/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "EmployeeLibrary", id: "CATEGORIES" }],
    }),
    getEmployeeLibraryResources: build.query<
      EmpResourceAdmin[],
      { categoryId?: string; q?: string }
    >({
      query: ({ categoryId, q }) => ({
        url: "/employee/admin/library/resources",
        params: { ...(categoryId ? { categoryId } : {}), ...(q ? { q } : {}) },
      }),
      transformResponse: unwrap<EmpResourceAdmin[]>,
      providesTags: [{ type: "EmployeeLibrary", id: "RESOURCES" }],
    }),
    // A file goes up as multipart with the details in the "resource" field.
    addEmployeeLibraryResource: build.mutation<
      EmpResourceAdmin,
      {
        resource: EmpResourceInput;
        file?: File | null;
        linkUrl?: string | null;
        /** A video uploaded beforehand (videoUpload.ts, purpose "resource"). */
        videoId?: string | null;
      }
    >({
      query: ({ resource, file, linkUrl, videoId }) => {
        const body = new FormData();
        body.append(
          "resource",
          JSON.stringify(
            file ? resource : videoId ? { ...resource, videoId } : { ...resource, linkUrl },
          ),
        );
        if (file) body.append("file", file);
        return { url: "/employee/admin/library/resources", method: "POST", body };
      },
      transformResponse: unwrap<EmpResourceAdmin>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
        { type: "EmployeeLibrary", id: "ACKS" },
        { type: "EmployeeLibrary", id: "REVIEW" },
        { type: "EmployeeLibrary", id: "CATEGORIES" },
      ],
    }),
    updateEmployeeLibraryResource: build.mutation<
      EmpResourceAdmin,
      { id: string; resource: EmpResourceInput }
    >({
      query: ({ id, resource }) => ({
        url: `/employee/admin/library/resources/${id}`,
        method: "PUT",
        body: resource,
      }),
      transformResponse: unwrap<EmpResourceAdmin>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
        { type: "EmployeeLibrary", id: "ACKS" },
        { type: "EmployeeLibrary", id: "REVIEW" },
        { type: "EmployeeLibrary", id: "CATEGORIES" },
      ],
    }),
    publishEmployeeLibraryVersion: build.mutation<
      EmpResourceAdmin,
      { id: string; file?: File | null; linkUrl?: string | null; videoId?: string | null }
    >({
      query: ({ id, file, linkUrl, videoId }) => {
        const body = new FormData();
        if (file) body.append("file", file);
        else if (videoId) body.append("version", JSON.stringify({ videoId }));
        else body.append("version", JSON.stringify({ linkUrl }));
        return { url: `/employee/admin/library/resources/${id}/versions`, method: "POST", body };
      },
      transformResponse: unwrap<EmpResourceAdmin>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
        { type: "EmployeeLibrary", id: "ACKS" },
        { type: "EmployeeLibrary", id: "REVIEW" },
      ],
    }),
    setEmployeeLibraryArchived: build.mutation<EmpResourceAdmin, { id: string; archived: boolean }>(
      {
        query: ({ id, archived }) => ({
          url: `/employee/admin/library/resources/${id}/${archived ? "archive" : "restore"}`,
          method: "POST",
        }),
        transformResponse: unwrap<EmpResourceAdmin>,
        invalidatesTags: [
          { type: "EmployeeLibrary", id: "RESOURCES" },
          { type: "EmployeeLibrary", id: "MINE" },
          { type: "EmployeeLibrary", id: "ACKS" },
          { type: "EmployeeLibrary", id: "REVIEW" },
        ],
      },
    ),
    /**
     * The cover a resource shows. Not a version: the picture changes, the
     * file and everyone's acknowledgements do not.
     */
    setEmployeeLibraryCover: build.mutation<EmpResourceAdmin, { id: string; file: File }>({
      query: ({ id, file }) => {
        const body = new FormData();
        body.append("cover", file);
        return { url: `/employee/admin/library/resources/${id}/cover`, method: "PUT", body };
      },
      transformResponse: unwrap<EmpResourceAdmin>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
      ],
    }),
    resetEmployeeLibraryCover: build.mutation<EmpResourceAdmin, string>({
      query: (id) => ({
        url: `/employee/admin/library/resources/${id}/cover`,
        method: "DELETE",
      }),
      transformResponse: unwrap<EmpResourceAdmin>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
      ],
    }),
    deleteEmployeeLibraryResource: build.mutation<void, string>({
      query: (id) => ({ url: `/employee/admin/library/resources/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
        { type: "EmployeeLibrary", id: "ACKS" },
        { type: "EmployeeLibrary", id: "REVIEW" },
        { type: "EmployeeLibrary", id: "CATEGORIES" },
      ],
    }),

    // ── My → Resources (everyone) ──
    getEmployeeMyResources: build.query<
      EmpResource[],
      { view: EmpResourceView; categoryId?: string; q?: string }
    >({
      query: ({ view, categoryId, q }) => ({
        url: "/employee/resources",
        params: { view, ...(categoryId ? { categoryId } : {}), ...(q ? { q } : {}) },
      }),
      transformResponse: unwrap<EmpResource[]>,
      providesTags: [{ type: "EmployeeLibrary", id: "MINE" }],
    }),
    getEmployeeReaderCategories: build.query<EmpReaderCategory[], void>({
      query: () => ({ url: "/employee/resources/categories" }),
      transformResponse: unwrap<EmpReaderCategory[]>,
      providesTags: [{ type: "EmployeeLibrary", id: "MINE" }],
    }),
    getEmployeeResourcesOutstanding: build.query<{ count: number }, void>({
      query: () => ({ url: "/employee/resources/outstanding" }),
      transformResponse: unwrap<{ count: number }>,
      providesTags: [{ type: "EmployeeLibrary", id: "MINE" }],
    }),
    // My → Resources → State regulations: skyComply's, the same for everyone.
    getEmployeeRegulations: build.query<EmpRegulationList, void>({
      query: () => ({ url: "/employee/regulations" }),
      transformResponse: unwrap<EmpRegulationList>,
    }),
    getEmployeeMyResource: build.query<EmpResource, string>({
      query: (id) => ({ url: `/employee/resources/${id}` }),
      transformResponse: unwrap<EmpResource>,
      providesTags: [{ type: "EmployeeLibrary", id: "MINE" }],
    }),
    setEmployeeResourceFavourite: build.mutation<void, { id: string; favourite: boolean }>({
      query: ({ id, favourite }) => ({
        url: `/employee/resources/${id}/favourite`,
        method: favourite ? "PUT" : "DELETE",
      }),
      invalidatesTags: [{ type: "EmployeeLibrary", id: "MINE" }],
    }),
    acknowledgeEmployeeResource: build.mutation<
      { acknowledgedAt: string },
      { id: string; version: number }
    >({
      query: ({ id, version }) => ({
        url: `/employee/resources/${id}/acknowledge`,
        method: "POST",
        body: { version, confirmed: true },
      }),
      transformResponse: unwrap<{ acknowledgedAt: string }>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "MINE" },
        { type: "EmployeeLibrary", id: "ACKS" },
      ],
    }),

    // ── Manage → Acknowledgements (Managers in scope, Admins) ──
    getEmployeeAckTracker: build.query<EmpAckTrackerRow[], { location?: string }>({
      query: ({ location }) => ({
        url: "/employee/manage/acks",
        params: location ? { location } : {},
      }),
      transformResponse: unwrap<EmpAckTrackerRow[]>,
      providesTags: [{ type: "EmployeeLibrary", id: "ACKS" }],
    }),
    getEmployeeAckDetail: build.query<EmpAckTrackerDetail, { id: string; location?: string }>({
      query: ({ id, location }) => ({
        url: `/employee/manage/acks/${id}`,
        params: location ? { location } : {},
      }),
      transformResponse: unwrap<EmpAckTrackerDetail>,
      providesTags: [{ type: "EmployeeLibrary", id: "ACKS" }],
    }),
    remindEmployeeAck: build.mutation<
      { reminded: number; alreadyToday: number },
      { id: string; location?: string }
    >({
      query: ({ id, location }) => ({
        url: `/employee/manage/acks/${id}/remind`,
        method: "POST",
        params: location ? { location } : {},
      }),
      transformResponse: unwrap<{ reminded: number; alreadyToday: number }>,
    }),
    // ── Review dates (Admins) ──
    getEmployeePastReview: build.query<EmpPastReview[], void>({
      query: () => ({ url: "/employee/admin/library/past-review" }),
      transformResponse: unwrap<EmpPastReview[]>,
      providesTags: [{ type: "EmployeeLibrary", id: "REVIEW" }],
    }),
    markEmployeeResourceReviewed: build.mutation<{ reviewMonth: string }, string>({
      query: (id) => ({ url: `/employee/admin/library/resources/${id}/reviewed`, method: "POST" }),
      transformResponse: unwrap<{ reviewMonth: string }>,
      invalidatesTags: [
        { type: "EmployeeLibrary", id: "REVIEW" },
        { type: "EmployeeLibrary", id: "RESOURCES" },
        { type: "EmployeeLibrary", id: "MINE" },
      ],
    }),
    remindEmployeeResourceOwner: build.mutation<void, string>({
      query: (id) => ({
        url: `/employee/admin/library/resources/${id}/remind-owner`,
        method: "POST",
      }),
    }),

    // ── Approvals (Admins) ──
    getEmployeeGiftApprovals: build.query<EmpAwardApproval[], void>({
      query: () => ({ url: "/employee/admin/giftcards/approvals" }),
      transformResponse: unwrap<EmpAwardApproval[]>,
      providesTags: [{ type: "EmployeeGiftcards", id: "APPROVALS" }],
    }),
    approveEmployeeAward: build.mutation<{ status: string }, string>({
      query: (id) => ({ url: `/employee/admin/giftcards/awards/${id}/approve`, method: "POST" }),
      transformResponse: unwrap<{ status: string }>,
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    declineEmployeeAward: build.mutation<void, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/employee/admin/giftcards/awards/${id}/decline`,
        method: "POST",
        body: { reason },
      }),
      // A declined award no longer counts against allowances or budgets.
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    getEmployeeGiftBudgets: build.query<EmpBudgets, { quarter: string }>({
      query: ({ quarter }) => ({ url: "/employee/admin/giftcards/budgets", params: { quarter } }),
      transformResponse: unwrap<EmpBudgets>,
      providesTags: [{ type: "EmployeeGiftcards", id: "BUDGETS" }],
    }),
    setEmployeeGiftBudget: build.mutation<
      void,
      { quarter: string; locationId: string | null; limitCents: number | null }
    >({
      query: (body) => ({ url: "/employee/admin/giftcards/budgets", method: "PUT", body }),
      invalidatesTags: GIFTCARDS_CHANGED,
    }),
    getEmployeeGiftAllowances: build.query<EmpManagerAllowance[], { quarter: string }>({
      query: ({ quarter }) => ({
        url: "/employee/admin/giftcards/allowances",
        params: { quarter },
      }),
      transformResponse: unwrap<EmpManagerAllowance[]>,
      providesTags: [{ type: "EmployeeGiftcards", id: "ALLOWANCES" }],
    }),
    setEmployeeGiftAllowance: build.mutation<
      void,
      { partyId: string; quarter: string; valueCents: number | null; awardCount: number | null }
    >({
      query: ({ partyId, ...body }) => ({
        url: `/employee/admin/giftcards/allowances/${partyId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: GIFTCARDS_CHANGED,
    }),

    // ── Coming up (Phase 4): milestones of people the caller can see ──
    getEmployeeComingUp: build.query<EmpMilestone[], { limit?: number } | void>({
      query: (arg) => ({
        url: "/employee/milestones/upcoming",
        params: arg?.limit ? { limit: arg.limit } : {},
      }),
      transformResponse: unwrap<EmpMilestone[]>,
      extraOptions: { retryCondition: retryTransientOnly },
    }),

    // ── Recognition received (Phase 4) ──
    getEmployeeMyRecognition: build.query<EmpRecognitionPage, { before?: string } | void>({
      query: (arg) => ({
        url: "/employee/me/recognition",
        params: arg?.before ? { before: arg.before } : {},
      }),
      transformResponse: unwrap<EmpRecognitionPage>,
      providesTags: [RECEIVED],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeMyBadgeCounts: build.query<EmpBadgeCount[], void>({
      query: () => ({ url: "/employee/me/recognition/badges" }),
      transformResponse: unwrap<EmpBadgeCount[]>,
      providesTags: [RECEIVED],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    /** My latest badges and how many in all (My profile). */
    getEmployeeMyRecentBadges: build.query<EmpRecentBadges, void>({
      query: () => ({ url: "/employee/me/recognition/badges/recent" }),
      transformResponse: unwrap<EmpRecentBadges>,
      providesTags: [RECEIVED],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    /** A colleague's latest badges and how many in all (their profile). */
    getEmployeePersonRecentBadges: build.query<EmpRecentBadges, string>({
      query: (partyId) => ({
        url: `/employee/people/${encodeURIComponent(partyId)}/badges/recent`,
      }),
      transformResponse: unwrap<EmpRecentBadges>,
      providesTags: [RECEIVED],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeLeaderboard: build.query<
      EmpLeaderboard,
      { period: EmpLeaderboardPeriod; badgeId?: string; locationId?: string }
    >({
      query: ({ period, badgeId, locationId }) => ({
        url: "/employee/recognition/leaderboard",
        params: {
          period,
          ...(badgeId ? { badgeId } : {}),
          ...(locationId ? { locationId } : {}),
        },
      }),
      transformResponse: unwrap<EmpLeaderboard>,
      providesTags: [RECEIVED],
      extraOptions: { retryCondition: retryTransientOnly },
    }),

    // ── E-cards (Phase 4): only ever the caller's own ──
    getEmployeeEcards: build.query<EmpEcardPage, { box: "received" | "sent"; before?: string }>({
      query: ({ box, before }) => ({
        url: `/employee/ecards/${box}`,
        params: before ? { before } : {},
      }),
      transformResponse: unwrap<EmpEcardPage>,
      providesTags: (_r, _e, { box, before }) =>
        before ? [] : [{ type: "EmployeeRecognition", id: `ECARDS_${box.toUpperCase()}` }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    sendEmployeeEcard: build.mutation<{ sent: number; sharedPostId: string | null }, EmpNewEcard>({
      query: (card) => ({ url: "/employee/ecards", method: "POST", body: card }),
      transformResponse: unwrap<{ sent: number; sharedPostId: string | null }>,
      invalidatesTags: (_r, _e, card) => [
        { type: "EmployeeRecognition", id: "ECARDS_SENT" },
        ...(card.share ? BOTH_FEEDS : []),
      ],
    }),
    // No invalidation: the cards on screen keep their "New" mark for this visit.
    markEmployeeEcardsRead: build.mutation<void, void>({
      query: () => ({ url: "/employee/ecards/read", method: "POST" }),
    }),

    // ── Home banners ──
    // `{ view: "manage" }`: the admin view's Home, with the banners the caller
    // manages too, each marked canManage.
    getEmployeeHomeBanners: build.query<EmpBanner[], { view?: "manage" } | void>({
      query: (args) => ({
        url: "/employee/banners",
        params: args?.view ? { view: args.view } : undefined,
      }),
      transformResponse: unwrap<EmpBanner[]>,
      providesTags: [{ type: "EmployeeBanners", id: "HOME" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeeManagedBanners: build.query<EmpManagedBanner[], void>({
      query: () => ({ url: "/employee/manage/banners" }),
      transformResponse: unwrap<EmpManagedBanner[]>,
      providesTags: [{ type: "EmployeeBanners", id: "MANAGE" }],
    }),
    /** What a banner can link to (the editor's picker), newest first. */
    getEmployeeBannerLinkOptions: build.query<
      EmpBannerLinkOption[],
      { kind: EmpBannerLinkKind; q?: string }
    >({
      query: ({ kind, q }) => ({
        url: "/employee/manage/banners/link-options",
        params: q ? { kind, q } : { kind },
      }),
      transformResponse: unwrap<EmpBannerLinkOption[]>,
      keepUnusedDataFor: 30,
    }),
    saveEmployeeBanner: build.mutation<
      EmpManagedBanner,
      { bannerId?: string; banner: EmpBannerInput; image?: File | null }
    >({
      query: ({ bannerId, banner, image }) => {
        const body = new FormData();
        body.append("banner", JSON.stringify(banner));
        if (image) body.append("image", image);
        return bannerId
          ? { url: `/employee/manage/banners/${bannerId}`, method: "PUT", body }
          : { url: "/employee/manage/banners", method: "POST", body };
      },
      transformResponse: unwrap<EmpManagedBanner>,
      invalidatesTags: ["EmployeeBanners"],
    }),
    endEmployeeBanner: build.mutation<EmpManagedBanner, string>({
      query: (bannerId) => ({ url: `/employee/manage/banners/${bannerId}/end`, method: "POST" }),
      transformResponse: unwrap<EmpManagedBanner>,
      invalidatesTags: ["EmployeeBanners"],
    }),
    deleteEmployeeBanner: build.mutation<void, string>({
      query: (bannerId) => ({ url: `/employee/manage/banners/${bannerId}`, method: "DELETE" }),
      invalidatesTags: ["EmployeeBanners"],
    }),
    reorderEmployeeBanners: build.mutation<void, string[]>({
      query: (ids) => ({ url: "/employee/manage/banners/order", method: "PUT", body: { ids } }),
      invalidatesTags: ["EmployeeBanners"],
    }),

    // ── The feed ──
    // The first page is cached (EmployeeFeed); older pages, likes, votes and
    // comments are kept on the page and patched in place, so the list never
    // jumps or refetches when someone likes a post.
    getEmployeeFeed: build.query<EmpFeedPage, EmployeeFeedArgs>({
      query: ({ type, media, before, view, locationId, recipientPartyId }) => ({
        url: "/employee/feed",
        params: {
          ...(type ? { type } : {}),
          ...(media ? { media } : {}),
          ...(recipientPartyId ? { recipientPartyId } : {}),
          ...(before ? { before } : {}),
          ...(view ? { view } : {}),
          ...(view && locationId ? { locationId } : {}),
        },
      }),
      transformResponse: unwrap<EmpFeedPage>,
      providesTags: (_r, _e, { before, view }) =>
        before ? [] : [view === "manage" ? ADMIN_FEED : EMPLOYEE_FEED],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    getEmployeePost: build.query<EmpFeedPost, string>({
      query: (postId) => ({ url: `/employee/posts/${encodeURIComponent(postId)}` }),
      transformResponse: unwrap<EmpFeedPost>,
      providesTags: (_r, _e, postId) => [{ type: "EmployeeFeed", id: postId }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    createEmployeePost: build.mutation<EmpFeedPost, { post: EmployeeNewPost; photos: File[] }>({
      query: ({ post, photos }) => {
        const body = new FormData();
        body.append("post", JSON.stringify(post));
        for (const photo of photos) body.append("photos", photo);
        return { url: "/employee/posts", method: "POST", body };
      },
      transformResponse: unwrap<EmpFeedPost>,
      // The feed and, for an announcement, spotlight or poll, Manage → Content.
      invalidatesTags: [...BOTH_FEEDS, "EmployeeContent", RECEIVED],
    }),
    createEmployeePoll: build.mutation<EmpFeedPost, EmployeeNewPoll>({
      query: (body) => ({ url: "/employee/polls", method: "POST", body }),
      transformResponse: unwrap<EmpFeedPost>,
      // The feed, and Manage → Content's Polls list it was created from.
      invalidatesTags: [...BOTH_FEEDS, "EmployeeContent", RECEIVED],
    }),
    editEmployeePost: build.mutation<
      EmpFeedPost,
      { postId: string; title?: string | null; body: string }
    >({
      query: ({ postId, ...body }) => ({ url: `/employee/posts/${postId}`, method: "PATCH", body }),
      transformResponse: unwrap<EmpFeedPost>,
      invalidatesTags: (_r, _e, { postId }) => [
        { type: "EmployeeFeed", id: postId },
        "EmployeeContent",
        // Its title shows on Featured videos.
        FEATURED_VIDEOS,
      ],
      async onQueryStarted({ postId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          patchCachedPost(dispatch, getState, postId, () => data);
        } catch {
          // Not saved: the cache is as it was.
        }
      },
    }),
    deleteEmployeePost: build.mutation<void, string>({
      query: (postId) => ({ url: `/employee/posts/${postId}`, method: "DELETE" }),
      // Its own page asks again (and finds it gone); the feed is patched below.
      // Survey results it shared can be shared again.
      invalidatesTags: (_r, error, postId) =>
        error
          ? []
          : [
              { type: "EmployeeFeed", id: postId },
              "EmployeeContent",
              RECEIVED,
              FEATURED_VIDEOS,
              "EmployeeSurveys",
            ],
      async onQueryStarted(postId, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchCachedPost(dispatch, getState, postId, () => null);
        } catch {
          // Not deleted: the cache is as it was.
        }
      },
    }),
    // ── Videos ──
    // Short-lived URLs a player loads from (a <video> can't send the bearer
    // header). Kept well inside their ten minutes; a player that finds them
    // lapsed asks again (refetch).
    getEmployeeVideoPlayback: build.query<EmpVideoPlayback, string>({
      query: (videoId) => ({ url: `/employee/videos/${encodeURIComponent(videoId)}/playback` }),
      transformResponse: unwrap<EmpVideoPlayback>,
      // Short: the URLs last ten minutes, and a player opened again asks afresh
      // (see refetchOnMountOrArgChange where it's used).
      keepUnusedDataFor: 60,
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    recordEmployeeVideoView: build.mutation<void, string>({
      query: (videoId) => ({ url: `/employee/videos/${videoId}/views`, method: "POST" }),
    }),
    getEmployeeFeaturedVideos: build.query<EmpFeedPost[], { limit?: number } | void>({
      query: (args) => ({
        url: "/employee/featured-videos",
        params: args?.limit ? { limit: args.limit } : {},
      }),
      transformResponse: unwrap<EmpFeedPost[]>,
      providesTags: [FEATURED_VIDEOS],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    featureEmployeeVideo: build.mutation<EmpFeedPost, string>({
      query: (postId) => ({ url: `/employee/posts/${postId}/feature`, method: "POST" }),
      transformResponse: unwrap<EmpFeedPost>,
      invalidatesTags: [FEATURED_VIDEOS],
      async onQueryStarted(postId, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          patchCachedPost(dispatch, getState, postId, (post) => ({
            ...post,
            featuredOrder: data.featuredOrder ?? null,
          }));
        } catch {
          // Not featured: the cache is as it was.
        }
      },
    }),
    unfeatureEmployeeVideo: build.mutation<void, string>({
      query: (postId) => ({ url: `/employee/posts/${postId}/feature`, method: "DELETE" }),
      invalidatesTags: [FEATURED_VIDEOS],
      async onQueryStarted(postId, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchCachedPost(dispatch, getState, postId, (post) => ({ ...post, featuredOrder: null }));
        } catch {
          // Still featured: the cache is as it was.
        }
      },
    }),
    reorderEmployeeFeaturedVideos: build.mutation<void, string[]>({
      query: (postIds) => ({
        url: "/employee/featured-videos/order",
        method: "PUT",
        body: { postIds },
      }),
      invalidatesTags: [FEATURED_VIDEOS],
    }),
    setEmployeeLike: build.mutation<
      EmpLikeState,
      { target: "post" | "comment"; id: string; liked: boolean }
    >({
      query: ({ target, id, liked }) => ({
        url: `/employee/${target === "post" ? "posts" : "comments"}/${id}/like`,
        method: liked ? "PUT" : "DELETE",
      }),
      transformResponse: unwrap<EmpLikeState>,
      // A post's like shows on the cached feed and post too, not just here.
      async onQueryStarted({ target, id }, { dispatch, getState, queryFulfilled }) {
        if (target !== "post") return;
        try {
          const { data } = await queryFulfilled;
          patchCachedPost(dispatch, getState, id, (post) => ({
            ...post,
            likedByMe: data.liked,
            likeCount: data.likeCount,
          }));
        } catch {
          // Not saved: the cache is as it was.
        }
      },
    }),
    getEmployeeLikers: build.query<
      EmpLikersPage,
      { target: "post" | "comment"; id: string; before?: string }
    >({
      query: ({ target, id, before }) => ({
        url: `/employee/${target === "post" ? "posts" : "comments"}/${id}/likes`,
        params: before ? { before } : undefined,
      }),
      transformResponse: unwrap<EmpLikersPage>,
      keepUnusedDataFor: 0,
    }),
    getEmployeeComments: build.query<EmpCommentPage, { postId: string; after?: string }>({
      query: ({ postId, after }) => ({
        url: `/employee/posts/${postId}/comments`,
        params: after ? { after } : undefined,
      }),
      transformResponse: unwrap<EmpCommentPage>,
      keepUnusedDataFor: 30,
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    addEmployeeComment: build.mutation<
      EmpComment,
      { postId: string; body: string; mentionPartyIds?: string[]; parentId?: string | null }
    >({
      query: ({ postId, ...body }) => ({
        url: `/employee/posts/${postId}/comments`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap<EmpComment>,
    }),
    editEmployeeComment: build.mutation<EmpComment, { commentId: string; body: string }>({
      query: ({ commentId, body }) => ({
        url: `/employee/comments/${commentId}`,
        method: "PATCH",
        body: { body },
      }),
      transformResponse: unwrap<EmpComment>,
    }),
    deleteEmployeeComment: build.mutation<void, string>({
      query: (commentId) => ({ url: `/employee/comments/${commentId}`, method: "DELETE" }),
    }),
    voteEmployeePoll: build.mutation<EmpFeedPost, { postId: string; optionId: string }>({
      query: ({ postId, optionId }) => ({
        url: `/employee/posts/${postId}/vote`,
        method: "PUT",
        body: { optionId },
      }),
      transformResponse: unwrap<EmpFeedPost>,
      async onQueryStarted({ postId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          patchCachedPost(dispatch, getState, postId, () => data);
        } catch {
          // Not saved: the cache is as it was.
        }
      },
    }),
    closeEmployeePoll: build.mutation<EmpFeedPost, string>({
      query: (postId) => ({ url: `/employee/posts/${postId}/close`, method: "POST" }),
      transformResponse: unwrap<EmpFeedPost>,
      invalidatesTags: ["EmployeeContent"],
      // Closed in Manage → Content: the cached feed and post show it closed too.
      async onQueryStarted(postId, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          patchCachedPost(dispatch, getState, postId, () => data);
        } catch {
          // Not saved: the cache is as it was.
        }
      },
    }),
    exportEmployeePollResults: build.mutation<string, string>({
      query: (postId) => ({
        url: `/employee/posts/${postId}/results.csv`,
        responseHandler: (response: Response) => response.text(),
      }),
      // A download someone just clicked: an error shows at once (they can
      // click again), not after ~14 s of retries.
      extraOptions: { retryCondition: () => false },
    }),
    reportEmployeeContent: build.mutation<
      void,
      { target: EmpReportTarget; id: string; reason: EmpReportReason; details?: string | null }
    >({
      query: ({ target, id, ...body }) => ({
        url: `/employee/reports/${target}/${id}`,
        method: "POST",
        body,
      }),
    }),
    moderateEmployeeContent: build.mutation<
      void,
      { target: EmpReportTarget; id: string; action: EmpModerationAction }
    >({
      query: ({ target, id, action }) => ({
        url: `/employee/moderation/${target}/${id}`,
        method: "POST",
        body: { action },
      }),
      // The queue and its badge; the feed card patches itself in place, and the
      // cached feed pages and post follow, for when the page is opened again.
      invalidatesTags: (_r, error, { target, id, action }) => [
        "EmployeeModeration",
        RECEIVED,
        "EmployeeContent",
        // Hiding or removing a post takes it off Featured videos.
        ...(!error && target === "post" ? [FEATURED_VIDEOS] : []),
        // A removed post's own page asks again (and finds it gone), and
        // survey results it shared can be shared again.
        ...(!error && target === "post" && action === "remove"
          ? [{ type: "EmployeeFeed" as const, id }, "EmployeeSurveys" as const]
          : []),
        // Shown in the feed again: the employee view's feed had dropped it,
        // so it asks again (the admin view's keeps it, patched below).
        ...(!error && target === "post" && action === "restore" ? [EMPLOYEE_FEED] : []),
      ],
      async onQueryStarted({ target, id, action }, { dispatch, getState, queryFulfilled }) {
        if (target !== "post" || action === "keep") return;
        try {
          await queryFulfilled;
          // A hidden post leaves every employee-view feed (as the server
          // leaves it out); only the admin view's feed keeps it, marked.
          patchCachedPost(dispatch, getState, id, (post, args) =>
            action === "remove" || (action === "hide" && args && args.view !== "manage")
              ? null
              : { ...post, hidden: action === "hide" },
          );
        } catch {
          // Not changed: the cache is as it was.
        }
      },
    }),

    // ── Manage → Content ──
    getEmployeeManagedPosts: build.query<
      EmpManagedPostPage,
      {
        types: EmpManagedPostType[];
        status?: EmpContentStatus;
        locationId?: string;
        before?: string;
      }
    >({
      query: ({ types, status, locationId, before }) => ({
        url: "/employee/manage/posts",
        params: {
          types: types.join(","),
          ...(status ? { status } : {}),
          ...(locationId ? { locationId } : {}),
          ...(before ? { before } : {}),
        },
      }),
      transformResponse: unwrap<EmpManagedPostPage>,
      providesTags: ["EmployeeContent"],
    }),
    changeEmployeeManagedPost: build.mutation<
      void,
      {
        postId: string;
        change: EmpManagedPostChange;
        /**
         * Made from a post in the feed: the cached feed pages are patched in
         * place instead of refetched, so an open feed (with "Show more" pages)
         * never jumps back to the top.
         */
        inPlace?: boolean;
      }
    >({
      query: ({ postId, change }) => ({
        url: `/employee/manage/posts/${postId}`,
        method: "PATCH",
        body: change,
      }),
      invalidatesTags: (_r, _e, { postId, inPlace, change }) => [
        "EmployeeContent",
        // Ended: survey results it shared can be shared again.
        ...(change.action === "end" ? ["EmployeeSurveys" as const] : []),
        // From the admin feed: that feed is patched in place, the employee
        // view's asks again (a pin moves a post to the top there).
        ...(inPlace ? [EMPLOYEE_FEED] : BOTH_FEEDS),
        { type: "EmployeeFeed", id: postId },
      ],
      async onQueryStarted({ postId, change, inPlace }, { dispatch, getState, queryFulfilled }) {
        if (!inPlace) return;
        try {
          await queryFulfilled;
          patchCachedPost(dispatch, getState, postId, (post) => {
            switch (change.action) {
              case "end":
                return null;
              case "pin":
                return { ...post, pinned: !!change.pinnedUntil };
              case "comments":
                return { ...post, commentsOff: change.commentsOff };
              default:
                return post;
            }
          });
        } catch {
          // Not changed: the cache is as it was.
        }
      },
    }),
    getEmployeeReportQueue: build.query<EmpReportQueuePage, { before?: string } | void>({
      query: (args) => ({
        url: "/employee/moderation/reports",
        params: args && args.before ? { before: args.before } : undefined,
      }),
      transformResponse: unwrap<EmpReportQueuePage>,
      providesTags: ["EmployeeModeration"],
    }),
    // The admin view's Home (P3-13).
    getEmployeeOverview: build.query<EmpOverview, void>({
      query: () => ({ url: "/employee/manage/overview" }),
      transformResponse: unwrap<EmpOverview>,
      providesTags: ["EmployeeContent", "EmployeeModeration", "EmployeeBanners"],
    }),
    getEmployeeOpenReportCount: build.query<number, void>({
      query: () => ({ url: "/employee/moderation/reports/count" }),
      transformResponse: (res: { data: { count: number } }) => res.data.count,
      providesTags: ["EmployeeModeration"],
    }),

    getEmployeeNotificationPrefs: build.query<EmpNotificationPrefs, void>({
      query: () => ({ url: "/employee/me/notification-prefs" }),
      transformResponse: unwrap<EmpNotificationPrefs>,
      providesTags: [{ type: "EmployeeNotifications", id: "PREFS" }],
      extraOptions: { retryCondition: retryTransientOnly },
    }),
    updateEmployeeNotificationPrefs: build.mutation<
      EmpNotificationPrefs,
      Partial<Pick<EmpNotificationPrefs, "mutedCategories" | "quietHours"> & { timeZone: string }>
    >({
      query: (body) => ({ url: "/employee/me/notification-prefs", method: "PUT", body }),
      transformResponse: unwrap<EmpNotificationPrefs>,
      invalidatesTags: [{ type: "EmployeeNotifications", id: "PREFS" }],
    }),
  }),
});

export const {
  useGetEmployeeMeQuery,
  useLazyGetEmployeeMeQuery,
  useGetEmployeeMyProfileQuery,
  useGetEmployeeMyScopeQuery,
  useGetEmployeeMyScopePeopleQuery,
  useGetEmployeeMembersQuery,
  useGetEmployeeMemberQuery,
  useSetEmployeeMemberStatusMutation,
  useGetEmployeeMemberScopeQuery,
  useReplaceEmployeeMemberScopeMutation,
  useGetEmployeeLocationsQuery,
  useCreateEmployeeLocationMutation,
  useUpdateEmployeeLocationMutation,
  useSetEmployeePersonLocationMutation,
  useGetEmployeePersonContactQuery,
  useGetEmployeeSettingsQuery,
  useUpdateEmployeeSettingsMutation,
  useGetEmployeeMessagingLimitsQuery,
  useGetEmployeeMessagingSettingsQuery,
  useGetTextingNumberQuery,
  useUpdateEmployeeMessagingSectionMutation,
  useSetUpEmployeePortalMutation,
  useUploadEmployeeOrgLogoMutation,
  useRemoveEmployeeOrgLogoMutation,
  useGetEmployeeDirectoryQuery,
  useGetEmployeeDirectoryFiltersQuery,
  useGetEmployeePersonQuery,
  useGetEmployeeOrgViewQuery,
  useGetEmployeeOrgPeopleQuery,
  useLazyGetEmployeeOrgReportsQuery,
  useUploadEmployeePhotoMutation,
  useRemoveEmployeePhotoMutation,
  useGetEmployeeNotificationsQuery,
  useLazyGetEmployeeNotificationsQuery,
  useGetEmployeeUnreadCountQuery,
  useMarkEmployeeNotificationsReadMutation,
  useMarkAllEmployeeNotificationsReadMutation,
  useGetEmployeeNotificationPrefsQuery,
  useUpdateEmployeeNotificationPrefsMutation,
  useSearchEmployeeQuery,
  useGetEmployeeMyBirthdayQuery,
  useSetEmployeeMyBirthdayMutation,
  useGetEmployeeBadgesQuery,
  useGetEmployeeEcardDesignsQuery,
  useGetEmployeeBadgesAdminQuery,
  useGetEmployeeEcardDesignsAdminQuery,
  useSaveEmployeeBadgeMutation,
  useSaveEmployeeEcardDesignMutation,
  useSetEmployeeRecognitionRetiredMutation,
  useDeleteEmployeeRecognitionItemMutation,
  useReorderEmployeeRecognitionMutation,
  useGetEmployeeComingUpQuery,
  useGetEmployeeRewardTypesAdminQuery,
  useSaveEmployeeRewardTypeMutation,
  useSetEmployeeRewardTypeRetiredMutation,
  useDeleteEmployeeRewardTypeMutation,
  useReorderEmployeeRewardTypesMutation,
  useGetEmployeeGiftSettingsQuery,
  useGetEmployeeGiftCodesQuery,
  useLazyGetEmployeeGiftCodesQuery,
  useLoadEmployeeGiftCodesMutation,
  useVoidEmployeeGiftCodesMutation,
  useUpdateEmployeeGiftSettingsMutation,
  useGetEmployeeGiftBudgetsQuery,
  useSetEmployeeGiftBudgetMutation,
  useGetEmployeeGiftAllowancesQuery,
  useSetEmployeeGiftAllowanceMutation,
  useGetEmployeeAwardableTypesQuery,
  useGetEmployeeMyAllowanceQuery,
  useGetEmployeeAwardsGivenQuery,
  useLazyGetEmployeeAwardsGivenQuery,
  useGiveEmployeeAwardMutation,
  useGetEmployeeGiftApprovalsQuery,
  useGetEmployeeMyRewardsQuery,
  useGetEmployeeLibraryCategoriesQuery,
  useGetEmployeeAckTrackerQuery,
  useGetEmployeeAckDetailQuery,
  useRemindEmployeeAckMutation,
  useGetEmployeePastReviewQuery,
  useMarkEmployeeResourceReviewedMutation,
  useRemindEmployeeResourceOwnerMutation,
  useGetEmployeeMyResourcesQuery,
  useGetEmployeeRegulationsQuery,
  useGetEmployeeReaderCategoriesQuery,
  useGetEmployeeResourcesOutstandingQuery,
  useGetEmployeeMyResourceQuery,
  useSetEmployeeResourceFavouriteMutation,
  useAcknowledgeEmployeeResourceMutation,
  useSaveEmployeeLibraryCategoryMutation,
  useDeleteEmployeeLibraryCategoryMutation,
  useGetEmployeeLibraryResourcesQuery,
  useAddEmployeeLibraryResourceMutation,
  useUpdateEmployeeLibraryResourceMutation,
  usePublishEmployeeLibraryVersionMutation,
  useSetEmployeeLibraryArchivedMutation,
  useSetEmployeeLibraryCoverMutation,
  useResetEmployeeLibraryCoverMutation,
  useDeleteEmployeeLibraryResourceMutation,
  useGetEmployeeGiftOverviewQuery,
  useGetEmployeeAwardsAdminQuery,
  useGetEmployeeAwardTotalsQuery,
  useLazyGetEmployeeAwardsAdminQuery,
  useGetEmployeeGiftAuditQuery,
  useLazyGetEmployeeGiftAuditQuery,
  useCancelEmployeeAwardMutation,
  useGetEmployeeFulfilmentQuery,
  useAdvanceEmployeeFulfilmentMutation,
  useRevealEmployeeCodeMutation,
  useApproveEmployeeAwardMutation,
  useDeclineEmployeeAwardMutation,
  useGetEmployeeMyRecognitionQuery,
  useLazyGetEmployeeMyRecognitionQuery,
  useGetEmployeeMyBadgeCountsQuery,
  useGetEmployeeMyRecentBadgesQuery,
  useGetEmployeePersonRecentBadgesQuery,
  useGetEmployeeLeaderboardQuery,
  useGetEmployeeEcardsQuery,
  useLazyGetEmployeeEcardsQuery,
  useSendEmployeeEcardMutation,
  useMarkEmployeeEcardsReadMutation,
  useGetEmployeeHomeBannersQuery,
  useGetEmployeeManagedBannersQuery,
  useGetEmployeeBannerLinkOptionsQuery,
  useLazyGetEmployeeManagedBannersQuery,
  useSaveEmployeeBannerMutation,
  useEndEmployeeBannerMutation,
  useDeleteEmployeeBannerMutation,
  useReorderEmployeeBannersMutation,
  useGetEmployeeMyAppsQuery,
  useGetEmployeeMyAppsAdminQuery,
  useSaveEmployeeExternalAppMutation,
  useDeleteEmployeeExternalAppMutation,
  useGetEmployeeFeedQuery,
  useLazyGetEmployeeFeedQuery,
  useGetEmployeePostQuery,
  useLazyGetEmployeePostQuery,
  useCreateEmployeePostMutation,
  useCreateEmployeePollMutation,
  useEditEmployeePostMutation,
  useDeleteEmployeePostMutation,
  useSetEmployeeLikeMutation,
  useGetEmployeeLikersQuery,
  useGetEmployeeCommentsQuery,
  useLazyGetEmployeeCommentsQuery,
  useAddEmployeeCommentMutation,
  useEditEmployeeCommentMutation,
  useDeleteEmployeeCommentMutation,
  useVoteEmployeePollMutation,
  useCloseEmployeePollMutation,
  useExportEmployeePollResultsMutation,
  useReportEmployeeContentMutation,
  useModerateEmployeeContentMutation,
  useGetEmployeeManagedPostsQuery,
  useLazyGetEmployeeManagedPostsQuery,
  useChangeEmployeeManagedPostMutation,
  useGetEmployeeReportQueueQuery,
  useLazyGetEmployeeReportQueueQuery,
  useGetEmployeeOpenReportCountQuery,
  useGetEmployeeOverviewQuery,
  // Surveys (Phase 7)
  useCreateEmployeeSurveyMutation,
  useUpdateEmployeeSurveyMutation,
  useAddEmployeeSurveyQuestionMutation,
  useDeleteEmployeeSurveyQuestionMutation,
  useScheduleEmployeeSurveyMutation,
  useUnscheduleEmployeeSurveyMutation,
  useUpdateEmployeeSurveyQuestionMutation,
  useReorderEmployeeSurveyQuestionsMutation,
  useCloseEmployeeSurveyMutation,
  useDeleteEmployeeSurveyMutation,
  useGetEmployeeSurveysQuery,
  useGetEmployeeSurveyQuery,
  useGetEmployeeSurveyResultsQuery,
  useExportEmployeeSurveyResultsMutation,
  useShareEmployeeSurveyResultsMutation,
  useGetEmployeeDueQuery,
  useGetEmployeeSurveyHistoryQuery,
  useGetEmployeeSurveyToAnswerQuery,
  useSaveEmployeeSurveyProgressMutation,
  useSubmitEmployeeSurveyMutation,
  useOptOutOfEmployeeSurveyMutation,
  // Messages (Phase 8)
  useGetEmployeeConversationsQuery,
  useGetEmployeeUnreadMessagesQuery,
  useGetEmployeeConversationQuery,
  useOpenEmployeeConversationMutation,
  useGetEmployeeMessagesQuery,
  useSendEmployeeMessageMutation,
  useEditEmployeeMessageMutation,
  useDeleteEmployeeMessageMutation,
  useDeleteEmployeeConversationMutation,
  useMarkEmployeeConversationReadMutation,
  useSendEmployeeTypingMutation,
  useSetEmployeeConversationFlagMutation,
  useUploadEmployeeAttachmentMutation,
  useUploadEmployeeVoiceNoteMutation,
  useReportEmployeeMessageMutation,
  useGetEmployeeMessageReportsQuery,
  useGetEmployeeOpenMessageReportCountQuery,
  useReviewEmployeeMessageReportMutation,
  useGetEmployeeVideoPlaybackQuery,
  useRecordEmployeeVideoViewMutation,
  useGetEmployeeFeaturedVideosQuery,
  useFeatureEmployeeVideoMutation,
  useUnfeatureEmployeeVideoMutation,
  useReorderEmployeeFeaturedVideosMutation,
} = employeeApi;

/** The `code` of an employee API error, if it is one. */
export function employeeErrorCode(error: unknown): EmpErrorCode | string | null {
  if (!error || typeof error !== "object" || !("data" in error)) return null;
  const data = (error as { data?: unknown }).data;
  if (data && typeof data === "object" && "code" in data) {
    const code = (data as { code?: unknown }).code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

/** The server's human-readable message for an employee API error, if any. */
export function employeeErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("data" in error)) return null;
  const data = (error as { data?: unknown }).data;
  if (data && typeof data === "object" && "error" in data) {
    const message = (data as { error?: unknown }).error;
    return typeof message === "string" ? message : null;
  }
  return null;
}
